/**
 * Iter 248 — Live Translator pour Aircall.
 *
 * Mode "assistant" : l'agent appuie sur "🎙 Parler" → enregistre 3-5s →
 * transcription Whisper + traduction GPT-5.2 → affichage 2 colonnes
 * (source FR, traduction EN) → bouton "🔊 Lire la traduction" (OpenAI TTS).
 *
 * Pendant l'appel : le menu d'historique en haut + 2 directions :
 *   - Agent (FR) → Contact (EN)
 *   - Contact (EN) → Agent (FR)
 *
 * Le hover MediaRecorder utilise audio/webm (Whisper supporte).
 */
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { useVoiceActivityDetection } from "../hooks/useVoiceActivityDetection";
import EndOfCallDialog from "../components/translator/EndOfCallDialog";
import CoachPanel from "../components/translator/CoachPanel";
import { useNavigate } from "react-router-dom";
import {
  Languages, Mic, MicOff, Volume2, Phone, PhoneOff, Loader2,
  ArrowLeftRight, History, MessageSquare, Send, Zap, Radio, Download, FileText,
} from "lucide-react";
import { toast } from "sonner";

const LANGS = [
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "en", label: "English", flag: "🇬🇧" },
];

export default function AircallTranslatorPage() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [sourceLang, setSourceLang] = useState("fr");
  const [targetLang, setTargetLang] = useState("en");
  // Iter 253 — Auto-detect spoken language (Whisper detects → backend swaps direction automatically)
  const [autoDetectLang, setAutoDetectLang] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [exchanges, setExchanges] = useState([]);
  const [recording, setRecording] = useState(null); // null | "agent_to_contact" | "contact_to_agent"
  const [processing, setProcessing] = useState(false);
  const [textInput, setTextInput] = useState("");
  const [playingId, setPlayingId] = useState(null);
  // Iter 249 — End-of-call dialog (analyze + link prospect)
  const [showEoc, setShowEoc] = useState(false);
  const [pendingEndSession, setPendingEndSession] = useState(null);

  // Iter 248.1 — Auto-loop VAD : la session écoute en continu et traduit/parle
  // automatiquement à chaque fin de phrase détectée par le silence.
  const [autoMode, setAutoMode] = useState(null); // null | "agent_to_contact" | "contact_to_agent" | "duo"
  const [autoTtsEnabled, setAutoTtsEnabled] = useState(true);
  // Iter 248.2 — Contact audio source selection (microphone / tab / virtual cable)
  const [contactAudioSource, setContactAudioSource] = useState("tab"); // "microphone" | "tab" | deviceId
  const [audioInputs, setAudioInputs] = useState([]);
  const autoDirectionRef = useRef(null);
  const sessionRef = useRef(null);
  useEffect(() => { sessionRef.current = session; }, [session]);

  // Enumerate audio input devices (for Virtual Audio Cable selection)
  useEffect(() => {
    (async () => {
      try {
        // We need a permission grant first to get device labels; one quick getUserMedia
        // is the cleanest way. If the user already granted it earlier, it's instant.
        const devices = await navigator.mediaDevices.enumerateDevices();
        setAudioInputs(devices.filter((d) => d.kind === "audioinput"));
      } catch (e) {
        console.warn("Could not enumerate audio devices", e);
      }
    })();
  }, []);

  const handlePhraseEnd = useCallback(async (blob) => {
    const sess = sessionRef.current;
    const direction = autoDirectionRef.current;
    if (!sess || !direction) return;
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append("session_id", sess.session_id);
      fd.append("direction", direction);
      fd.append("audio", blob, "chunk.webm");
      const r = await api.post("/aircall/translator/transcribe-translate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (r.data.empty) return; // silence detected by Whisper too
      setExchanges((prev) => [...prev, { ...r.data, direction }]);
      // Auto-play TTS if enabled
      if (autoTtsEnabled && r.data.translation) {
        await playTranslationDirect(r.data);
      }
    } catch (e) {
      console.error("Auto-loop error", e);
    } finally {
      setProcessing(false);
    }
  }, [api, autoTtsEnabled]);

  const vad = useVoiceActivityDetection({
    threshold: 0.018,
    silenceMs: 1100,
    minSpeechMs: 400,
    onPhraseEnd: handlePhraseEnd,
  });

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Start session
  const startSession = async () => {
    if (sourceLang === targetLang) {
      toast.error("Langues source et cible doivent être différentes");
      return;
    }
    try {
      const r = await api.post("/aircall/translator/sessions", {
        source_lang: sourceLang,
        target_lang: targetLang,
        auto_detect_lang: autoDetectLang,
        contact_name: contactName,
        contact_phone: contactPhone,
      });
      setSession(r.data);
      setExchanges([]);
      toast.success("Session démarrée");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // End session
  const endSession = async () => {
    if (!session) return;
    // Iter 249 — If there are exchanges, show the EOC dialog first.
    if (exchanges.length > 0) {
      setPendingEndSession(session);
      setShowEoc(true);
      return;
    }
    // No exchanges → finalize immediately
    try {
      await api.post(`/aircall/translator/sessions/${session.session_id}/end`);
      toast.success("Session terminée");
      setSession(null);
      setExchanges([]);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // Iter 249 — Called when EOC dialog closes (any mode: link/create/skip)
  const finalizeAfterEoc = async (linkResult) => {
    if (!pendingEndSession) return;
    try {
      await api.post(`/aircall/translator/sessions/${pendingEndSession.session_id}/end`);
    } catch (e) {
      console.warn("end session failed:", e);
    }
    setShowEoc(false);
    setPendingEndSession(null);
    setSession(null);
    setExchanges([]);
    // If a prospect was linked/created, offer to navigate to its sheet
    if (linkResult?.prospect_id) {
      toast.success("Vous pouvez consulter la fiche prospect ↗", {
        action: {
          label: "Ouvrir la fiche",
          onClick: () => navigate(`/admin/prospects/${linkResult.prospect_id}`),
        },
        duration: 6000,
      });
    } else {
      toast.success("Session terminée");
    }
  };

  const cancelEoc = () => {
    setShowEoc(false);
    setPendingEndSession(null);
  };

  // Record audio chunk
  const startRecording = async (direction) => {
    if (!session) {
      toast.error("Démarrez d'abord une session");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await processChunk(blob, direction);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(direction);
    } catch (e) {
      toast.error("Impossible d'accéder au micro");
      console.error(e);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(null);
  };

  const processChunk = async (blob, direction) => {
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append("session_id", session.session_id);
      fd.append("direction", direction);
      fd.append("audio", blob, "chunk.webm");
      const r = await api.post("/aircall/translator/transcribe-translate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (r.data.empty) {
        toast.warning("Aucune parole détectée");
        return;
      }
      setExchanges((prev) => [...prev, { ...r.data, direction }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur transcription");
    } finally {
      setProcessing(false);
    }
  };

  // Text input fallback
  const sendText = async (direction) => {
    if (!session || !textInput.trim()) return;
    setProcessing(true);
    try {
      const r = await api.post("/aircall/translator/translate-text", {
        session_id: session.session_id,
        text: textInput.trim(),
        direction,
      });
      setExchanges((prev) => [...prev, { ...r.data, direction }]);
      setTextInput("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setProcessing(false);
    }
  };

  // Play translation as audio
  const playTranslationDirect = async (exchange) => {
    try {
      const r = await api.post("/aircall/translator/tts", {
        text: exchange.translation,
        target_lang: exchange.lang_target,
        voice: "nova",
      }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const audio = new Audio(url);
      return new Promise((resolve) => {
        audio.onended = () => { URL.revokeObjectURL(url); resolve(); };
        audio.onerror = () => { URL.revokeObjectURL(url); resolve(); };
        audio.play();
      });
    } catch (e) {
      console.error("TTS failed", e);
    }
  };

  const playTranslation = async (exchange) => {
    setPlayingId(exchange.exchange_id);
    await playTranslationDirect(exchange);
    setPlayingId(null);
  };

  // Iter 248.3 — Download transcript (TXT/PDF)
  const downloadTranscript = async (format) => {
    if (!session) return;
    if (!exchanges.length) {
      toast.warning("Aucun échange à exporter pour l'instant");
      return;
    }
    try {
      const r = await api.get(
        `/aircall/translator/sessions/${session.session_id}/transcript.${format}`,
        { responseType: "blob" },
      );
      const blob = new Blob([r.data], {
        type: format === "pdf" ? "application/pdf" : "text/plain;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `transcript_${session.session_id}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success(`Transcript ${format.toUpperCase()} téléchargé`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Échec du téléchargement ${format.toUpperCase()}`);
    }
  };

  // Iter 248.1 — Auto-loop start/stop
  const startAutoLoop = async (direction) => {
    if (!session) {
      toast.error("Démarrez d'abord une session");
      return;
    }
    if (autoMode) {
      stopAutoLoop();
      return;
    }
    try {
      autoDirectionRef.current = direction;
      setAutoMode(direction);
      // Iter 248.2 — Choose audio source based on direction:
      // - agent_to_contact : always the local microphone (you speaking)
      // - contact_to_agent : use the configured contactAudioSource (tab share / virtual cable / mic fallback)
      let source = "microphone";
      if (direction === "contact_to_agent") {
        if (contactAudioSource === "tab") source = "tab";
        else if (contactAudioSource && contactAudioSource !== "microphone") source = { deviceId: contactAudioSource };
      }
      await vad.start(source);
      const sourceLabel = source === "tab" ? "partage d'onglet"
        : (typeof source === "object" ? "périphérique sélectionné" : "micro");
      toast.success(`Mode auto activé (${sourceLabel}) : parlez naturellement`);
    } catch (e) {
      toast.error(e.message || "Impossible d'accéder à la source audio");
      setAutoMode(null);
      autoDirectionRef.current = null;
    }
  };

  const stopAutoLoop = () => {
    vad.stop();
    setAutoMode(null);
    autoDirectionRef.current = null;
    toast.success("Mode automatique arrêté");
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => { vad.stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const swapLangs = () => {
    setSourceLang(targetLang);
    setTargetLang(sourceLang);
  };

  return (
    <div className="p-6 max-w-[1300px] mx-auto space-y-5" data-testid="translator-page">
      <PageHeader
        icon={<Languages />}
        title="Traducteur en direct (Aircall)"
        description="Assistant temps réel FR ↔ EN pendant vos appels. Whisper + GPT-5.2 + OpenAI TTS."
      />

      {/* Setup pré-session */}
      {!session && (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-6 space-y-4" data-testid="setup-panel">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider">Configuration de la session</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-slate-500 uppercase mb-1 block">Votre langue (agent)</label>
              <select value={sourceLang} onChange={(e) => setSourceLang(e.target.value)} className="w-full bg-[#0b0811] border border-white/10 rounded px-3 py-2 text-sm text-white" data-testid="source-lang">
                {LANGS.map((l) => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <label className="text-[11px] text-slate-500 uppercase mb-1 block">Langue du contact</label>
                <select value={targetLang} onChange={(e) => setTargetLang(e.target.value)} className="w-full bg-[#0b0811] border border-white/10 rounded px-3 py-2 text-sm text-white" data-testid="target-lang">
                  {LANGS.map((l) => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
                </select>
              </div>
              <Button onClick={swapLangs} variant="ghost" size="icon" className="mt-5" data-testid="swap-langs">
                <ArrowLeftRight className="w-4 h-4" />
              </Button>
            </div>
            <div>
              <label className="text-[11px] text-slate-500 uppercase mb-1 block">Nom du contact (optionnel)</label>
              <input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="John Smith" className="w-full bg-[#0b0811] border border-white/10 rounded px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="text-[11px] text-slate-500 uppercase mb-1 block">Téléphone (optionnel)</label>
              <input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="+1 555 123 4567" className="w-full bg-[#0b0811] border border-white/10 rounded px-3 py-2 text-sm text-white" />
            </div>
            {/* Iter 253 — Auto-detect spoken language toggle */}
            <label className="flex items-start gap-2 p-2.5 rounded border border-purple-500/20 bg-purple-500/5 cursor-pointer hover:border-purple-500/40 transition-colors" data-testid="auto-detect-toggle">
              <input
                type="checkbox"
                checked={autoDetectLang}
                onChange={(e) => setAutoDetectLang(e.target.checked)}
                className="mt-0.5 accent-purple-400"
                data-testid="auto-detect-checkbox"
              />
              <div className="flex-1">
                <div className="text-xs text-white font-medium">🎤 Détecter automatiquement la langue parlée</div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  Whisper identifie la langue de chaque échange ; la direction est ajustée toute seule.
                  Idéal pour les conversations multilingues ou si vous ne savez pas qui va parler en premier.
                </div>
              </div>
            </label>
          </div>
          <Button onClick={startSession} className="bg-[#f7c935] hover:bg-[#e6b825] text-[#14101c] w-full" data-testid="btn-start-session">
            <Phone className="w-4 h-4 mr-2" /> Démarrer la session de traduction
          </Button>
        </div>
      )}

      {/* Session active */}
      {session && (
        <>
          {/* Status bar */}
          <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-xl p-4 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                <span className="text-sm font-bold text-white">Session active</span>
                <span className="text-xs text-slate-400">
                  {LANGS.find((l) => l.code === session.source_lang)?.flag} {session.source_lang.toUpperCase()}
                  {" ↔ "}
                  {LANGS.find((l) => l.code === session.target_lang)?.flag} {session.target_lang.toUpperCase()}
                </span>
                {session.contact_name && <span className="text-xs text-slate-500">· {session.contact_name}</span>}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">{exchanges.length} échange(s) traduit(s)</div>
            </div>
            <Button onClick={endSession} variant="outline" size="sm" className="text-red-400 border-red-500/30" data-testid="btn-end-session">
              <PhoneOff className="w-4 h-4 mr-1.5" /> Terminer
            </Button>
          </div>

          {/* Iter 248.1 — AUTO-LOOP MODE (VAD + auto-TTS) */}
          <div className={`rounded-xl p-5 border ${autoMode ? "bg-purple-500/5 border-purple-500/30" : "bg-[#14101c] border-white/10"}`} data-testid="auto-loop-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${autoMode ? "bg-purple-500/20 text-purple-400" : "bg-white/5 text-slate-400"}`}>
                  <Zap className={`w-5 h-5 ${autoMode ? "animate-pulse" : ""}`} />
                </div>
                <div>
                  <div className="font-bold text-white text-sm">Mode automatique (recommandé)</div>
                  <div className="text-xs text-slate-400">
                    {autoMode
                      ? <>🎙 <span className={vad.isSpeaking ? "text-emerald-400 font-bold" : "text-slate-500"}>{vad.isSpeaking ? "EN ÉCOUTE…" : "Silence"}</span> · Traduction & lecture vocale automatique à chaque fin de phrase</>
                      : "Parlez en continu, le système détecte les pauses, traduit et joue automatiquement la traduction."}
                  </div>
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoTtsEnabled}
                  onChange={(e) => setAutoTtsEnabled(e.target.checked)}
                  className="accent-purple-500"
                  data-testid="auto-tts-toggle"
                />
                <Volume2 className="w-3.5 h-3.5" />
                Lecture vocale auto
              </label>
            </div>

            {/* Iter 248.2 — Source audio pour le contact */}
            <div className="bg-black/30 border border-white/5 rounded-lg p-3 mb-3" data-testid="contact-source-selector">
              <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 block">
                Comment capturer la voix du contact ?
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <button
                  onClick={() => setContactAudioSource("tab")}
                  disabled={autoMode !== null}
                  className={`p-3 rounded border text-left text-xs transition-colors ${contactAudioSource === "tab" ? "border-purple-500/50 bg-purple-500/10 text-white" : "border-white/10 bg-white/[0.02] text-slate-400 hover:border-white/20"}`}
                  data-testid="source-tab"
                >
                  <div className="font-bold mb-0.5">🖥 Partage d'onglet</div>
                  <div className="text-[10px] opacity-70">Aircall Web, Zoom, Teams (Chrome/Edge)</div>
                </button>
                <button
                  onClick={() => setContactAudioSource("microphone")}
                  disabled={autoMode !== null}
                  className={`p-3 rounded border text-left text-xs transition-colors ${contactAudioSource === "microphone" ? "border-purple-500/50 bg-purple-500/10 text-white" : "border-white/10 bg-white/[0.02] text-slate-400 hover:border-white/20"}`}
                  data-testid="source-mic"
                >
                  <div className="font-bold mb-0.5">🎙 Micro de la pièce</div>
                  <div className="text-[10px] opacity-70">Casque/téléphone à proximité de votre micro</div>
                </button>
                <div className="relative">
                  <button
                    onClick={() => {
                      const firstCable = audioInputs.find((d) => /cable|virtual|blackhole|loopback|vb-audio/i.test(d.label));
                      if (firstCable) setContactAudioSource(firstCable.deviceId);
                      else setContactAudioSource(audioInputs[0]?.deviceId || "microphone");
                    }}
                    disabled={autoMode !== null || audioInputs.length === 0}
                    className={`w-full p-3 rounded border text-left text-xs transition-colors ${(contactAudioSource !== "tab" && contactAudioSource !== "microphone") ? "border-purple-500/50 bg-purple-500/10 text-white" : "border-white/10 bg-white/[0.02] text-slate-400 hover:border-white/20"}`}
                    data-testid="source-virtual-cable"
                  >
                    <div className="font-bold mb-0.5">🔌 Virtual Audio Cable</div>
                    <div className="text-[10px] opacity-70">Aircall Desktop, téléphone IP (setup 5 min)</div>
                  </button>
                </div>
              </div>
              {/* Device picker when virtual cable selected */}
              {contactAudioSource !== "tab" && contactAudioSource !== "microphone" && audioInputs.length > 0 && (
                <div className="mt-2">
                  <select
                    value={contactAudioSource}
                    onChange={(e) => setContactAudioSource(e.target.value)}
                    disabled={autoMode !== null}
                    className="w-full bg-[#0b0811] border border-white/10 rounded px-2 py-1.5 text-xs text-white"
                    data-testid="device-picker"
                  >
                    {audioInputs.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label || `Périphérique ${d.deviceId.slice(0, 8)}`}
                      </option>
                    ))}
                  </select>
                  <details className="mt-2">
                    <summary className="text-[10px] text-cyan-400 cursor-pointer hover:underline">
                      📘 Comment installer Virtual Audio Cable (5 min)
                    </summary>
                    <div className="mt-2 p-2 bg-black/40 rounded text-[11px] text-slate-300 space-y-1">
                      <div><strong className="text-white">Windows :</strong> Téléchargez <a href="https://vb-audio.com/Cable/" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline">VB-Cable</a> (gratuit) → installez → dans Aircall Desktop, sélectionnez "CABLE Input" comme haut-parleur → ici, sélectionnez "CABLE Output" comme entrée.</div>
                      <div><strong className="text-white">Mac :</strong> Installez <a href="https://existential.audio/blackhole/" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline">BlackHole 2ch</a> (gratuit) → créez un "Multi-Output Device" dans Audio MIDI Setup → Aircall utilise BlackHole, vous le sélectionnez ici.</div>
                      <div className="text-amber-400">💡 Vous entendrez quand même le contact dans vos écouteurs (configuration multi-sortie).</div>
                    </div>
                  </details>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Button
                onClick={() => startAutoLoop("agent_to_contact")}
                disabled={recording !== null || (autoMode && autoMode !== "agent_to_contact")}
                className={autoMode === "agent_to_contact" ? "bg-purple-500 hover:bg-purple-400 text-white animate-pulse" : "bg-blue-500 hover:bg-blue-400 text-white"}
                data-testid="btn-auto-agent"
              >
                {autoMode === "agent_to_contact" ? <Radio className="w-4 h-4 mr-2 animate-pulse" /> : <Zap className="w-4 h-4 mr-2" />}
                {autoMode === "agent_to_contact"
                  ? `Arrêter (${LANGS.find((l) => l.code === session.source_lang)?.flag} → ${LANGS.find((l) => l.code === session.target_lang)?.flag})`
                  : `Auto : Vous parlez 🎙 (${LANGS.find((l) => l.code === session.source_lang)?.flag} → ${LANGS.find((l) => l.code === session.target_lang)?.flag})`}
              </Button>
              <Button
                onClick={() => startAutoLoop("contact_to_agent")}
                disabled={recording !== null || (autoMode && autoMode !== "contact_to_agent")}
                className={autoMode === "contact_to_agent" ? "bg-purple-500 hover:bg-purple-400 text-white animate-pulse" : "bg-emerald-500 hover:bg-emerald-400 text-white"}
                data-testid="btn-auto-contact"
              >
                {autoMode === "contact_to_agent" ? <Radio className="w-4 h-4 mr-2 animate-pulse" /> : <Zap className="w-4 h-4 mr-2" />}
                {autoMode === "contact_to_agent"
                  ? `Arrêter (${LANGS.find((l) => l.code === session.target_lang)?.flag} → ${LANGS.find((l) => l.code === session.source_lang)?.flag})`
                  : `Auto : Contact parle 🔊 (${LANGS.find((l) => l.code === session.target_lang)?.flag} → ${LANGS.find((l) => l.code === session.source_lang)?.flag})`}
              </Button>
            </div>
          </div>

          {/* Iter 250 — AI Coach panel */}
          <CoachPanel
            api={api}
            session={session}
            onUseSuggestion={(text) => setTextInput(text)}
            onSpeakSuggestion={async (text, lang) => {
              try {
                const r = await api.post("/aircall/translator/tts", {
                  text, target_lang: lang, voice: "nova",
                }, { responseType: "blob" });
                const url = URL.createObjectURL(r.data);
                const a = new Audio(url); a.onended = () => URL.revokeObjectURL(url); a.play();
              } catch (e) { toast.error("Lecture impossible"); }
            }}
          />

          {/* Mode manuel (push-to-talk) - pour cas spéciaux où l'auto-loop n'est pas idéal */}
          <details className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid="manual-mode-section">            <summary className="text-xs text-slate-500 cursor-pointer flex items-center gap-2 hover:text-slate-300">
              <Mic className="w-3.5 h-3.5" />
              Mode manuel (push-to-talk) — Cliquer pour déployer
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3" data-testid="recording-controls">
            <RecordingPanel
              label="Vous parlez"
              subLabel={`(${LANGS.find((l) => l.code === session.source_lang)?.flag} → ${LANGS.find((l) => l.code === session.target_lang)?.flag})`}
              direction="agent_to_contact"
              recording={recording}
              processing={processing}
              onStart={() => startRecording("agent_to_contact")}
              onStop={stopRecording}
              color="#3b82f6"
            />
            <RecordingPanel
              label="Contact parle"
              subLabel={`(${LANGS.find((l) => l.code === session.target_lang)?.flag} → ${LANGS.find((l) => l.code === session.source_lang)?.flag})`}
              direction="contact_to_agent"
              recording={recording}
              processing={processing}
              onStart={() => startRecording("contact_to_agent")}
              onStop={stopRecording}
              color="#10b981"
            />
            </div>
          </details>

          {/* Text input fallback */}
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid="text-fallback">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-slate-500" />
              <input
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="Ou tapez le texte à traduire..."
                className="flex-1 bg-[#0b0811] border border-white/10 rounded px-3 py-2 text-sm text-white"
                onKeyDown={(e) => e.key === "Enter" && sendText("agent_to_contact")}
                data-testid="text-input"
              />
              <Button onClick={() => sendText("agent_to_contact")} disabled={!textInput.trim() || processing} size="sm" data-testid="btn-send-agent">
                <Send className="w-3.5 h-3.5 mr-1.5" />
                {LANGS.find((l) => l.code === session.source_lang)?.flag} → {LANGS.find((l) => l.code === session.target_lang)?.flag}
              </Button>
              <Button onClick={() => sendText("contact_to_agent")} disabled={!textInput.trim() || processing} size="sm" variant="outline" data-testid="btn-send-contact">
                <Send className="w-3.5 h-3.5 mr-1.5" />
                {LANGS.find((l) => l.code === session.target_lang)?.flag} → {LANGS.find((l) => l.code === session.source_lang)?.flag}
              </Button>
            </div>
          </div>

          {/* Exchanges history */}
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid="exchanges-list">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-slate-500" />
                <h3 className="text-sm font-bold text-white">Historique de la conversation</h3>
                <span className="text-[11px] text-slate-500">({exchanges.length} échange{exchanges.length > 1 ? "s" : ""})</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => downloadTranscript("txt")}
                  disabled={!exchanges.length}
                  size="sm"
                  variant="outline"
                  className="text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/10"
                  data-testid="btn-download-txt"
                >
                  <FileText className="w-3.5 h-3.5 mr-1.5" /> TXT
                </Button>
                <Button
                  onClick={() => downloadTranscript("pdf")}
                  disabled={!exchanges.length}
                  size="sm"
                  variant="outline"
                  className="text-purple-400 border-purple-500/30 hover:bg-purple-500/10"
                  data-testid="btn-download-pdf"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
                </Button>
              </div>
            </div>
            {exchanges.length === 0 ? (
              <div className="text-center text-sm text-slate-500 py-8">
                Cliquez sur "🎙 Vous parlez" ou "🎙 Contact parle" pour commencer la traduction.
              </div>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto">
                {exchanges.map((ex) => (
                  <ExchangeBubble key={ex.exchange_id} exchange={ex} onPlay={() => playTranslation(ex)} playing={playingId === ex.exchange_id} />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Iter 249 — End-of-call analysis + prospect linking */}
      {showEoc && pendingEndSession && (
        <EndOfCallDialog
          api={api}
          session={pendingEndSession}
          onClose={cancelEoc}
          onLinked={finalizeAfterEoc}
        />
      )}
    </div>
  );
}

function RecordingPanel({ label, subLabel, recording, processing, onStart, onStop, color, direction }) {
  const isActive = recording === direction;
  return (
    <div className={`rounded-xl p-5 border transition-colors ${isActive ? "border-red-500/40 bg-red-500/5" : "border-white/5 bg-[#14101c]"}`} data-testid={`panel-${direction}`}>
      <div className="mb-3">
        <div className="text-xs text-slate-400 uppercase tracking-wider">{label}</div>
        <div className="text-[10px] text-slate-600">{subLabel}</div>
      </div>
      {!isActive ? (
        <Button onClick={onStart} disabled={recording !== null || processing} className="w-full" style={{ background: color, color: "#0b0811" }} data-testid={`btn-record-${direction}`}>
          {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mic className="w-4 h-4 mr-2" />}
          Parler
        </Button>
      ) : (
        <Button onClick={onStop} className="w-full bg-red-500 hover:bg-red-400 text-white animate-pulse" data-testid={`btn-stop-${direction}`}>
          <MicOff className="w-4 h-4 mr-2" /> Arrêter
        </Button>
      )}
    </div>
  );
}

function ExchangeBubble({ exchange, onPlay, playing }) {
  const isAgent = exchange.direction === "agent_to_contact";
  return (
    <div className={`flex ${isAgent ? "justify-start" : "justify-end"}`} data-testid={`exchange-${exchange.exchange_id}`}>
      <div className={`max-w-[80%] rounded-lg p-3 ${isAgent ? "bg-blue-500/10 border-blue-500/30 border" : "bg-emerald-500/10 border-emerald-500/30 border"}`}>
        <div className="text-[10px] uppercase text-slate-500 mb-1">
          {isAgent ? "Vous" : "Contact"} · {exchange.lang_source.toUpperCase()} → {exchange.lang_target.toUpperCase()}
        </div>
        <div className="text-sm text-white">{exchange.transcript}</div>
        <div className="mt-2 pt-2 border-t border-white/5">
          <div className="text-[10px] uppercase text-slate-500 mb-1">Traduction ({exchange.lang_target.toUpperCase()})</div>
          <div className="flex items-start gap-2">
            <div className="flex-1 text-sm text-white font-medium">{exchange.translation}</div>
            <button
              onClick={onPlay}
              disabled={playing}
              className="p-1.5 rounded bg-white/5 hover:bg-white/10 text-cyan-400"
              title="Lire à voix haute"
              data-testid={`play-${exchange.exchange_id}`}
            >
              {playing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
